import {
  Component,
  computed,
  effect,
  inject,
  model,
  OnInit,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  IReservaRecursoData,
  IReservaData,
  IDisponibilidadData,
} from '@core/interfaces/entidades/reserva.entidad';
import { IFiltrarReservasReq } from '@core/interfaces/request-response/reserva.dto.interface';
import { EEstadoReserva, DIAS_SEMANA_LABELS } from '@core/enums/reserva.enum';
import { ReservaApiService } from '@core/services/api/reserva-api.service';
import { ToastService } from '@shared/services/toast.service';
import { AppService } from '@shared/services/app.service';
import { SucursalService } from '@shared/services/sucursal.service';
import { ModalConfirmacion } from '@shared/components/modal-confirmacion/modal-confirmacion';
import { CrearRecurso } from '@shared/components/crear-recurso/crear-recurso';
import { EditarRecurso } from '@shared/components/editar-recurso/editar-recurso';
import { CrearReserva } from '@shared/components/crear-reserva/crear-reserva';
import { ConfigHorarios } from '@shared/components/config-horarios/config-horarios';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  UiButton,
  UiDialog,
  UiSelect,
  UiTabs,
  UiTabPanel,
  UiDatepicker,
  UiSkeleton,
  type TabItem,
} from '@shared/components/ui';

@Component({
  selector: 'app-reservas',
  imports: [
    CommonModule,
    FormsModule,
    UiButton,
    UiDialog,
    UiSelect,
    UiTabs,
    UiTabPanel,
    UiDatepicker,
    UiSkeleton,
    ModalConfirmacion,
    CrearRecurso,
    EditarRecurso,
    CrearReserva,
    ConfigHorarios,
  ],
  templateUrl: './reservas.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './reservas.css',
})
export class Reservas implements OnInit {
  constructor() {
    effect(() => {
      if (this.sucursalService.sucursalReady() && this.appService.authReady()) {
        if (!this.recursosLoaded) {
          this.recursosLoaded = true;
          this.cargarRecursos();
        }
        if (this.tabActivo() === 'reservas') this.cargarReservas();
        if (this.tabActivo() === 'agenda') this.cargarAgendaActiva();
      }
    });
  }

  private recursosLoaded = false;

  ngOnInit(): void {
    this.appService.setPanelHeader({
      title: 'Gestión de Reservas',
      subtitle: 'Administra recursos, horarios y reservas de tu negocio.',
      showBackButton: true,
      icon: 'bi bi-calendar-check',
    });
  }

  private api = inject(ReservaApiService);
  private toast = inject(ToastService);
  private appService = inject(AppService);
  private sucursalService = inject(SucursalService);

  // Persistent tab
  tabActivo = signal<string>('recursos');

  readonly tabs: TabItem[] = [
    { value: 'recursos', label: 'Recursos', icon: 'bi bi-box' },
    { value: 'reservas', label: 'Reservas', icon: 'bi bi-list-ul' },
    { value: 'agenda', label: 'Agenda', icon: 'bi bi-calendar-week' },
  ];

  // Loading states
  loadingRecursos = signal(true);
  loadingReservas = signal(true);

  // Data
  recursos = signal<IReservaRecursoData[]>([]);
  recursoSeleccionado = signal<IReservaRecursoData | undefined>(undefined);
  reservas = signal<IReservaData[]>([]);
  disponibilidad = signal<IDisponibilidadData | null>(null);

  // Filters
  estadoFiltro = signal<string | null>(null);
  recursoFiltro = signal<number | null>(null);
  searchRecurso = model<string>('');

  // Agenda
  fechaAgenda = signal<string>(new Date().toISOString().split('T')[0]);
  vistaAgenda = signal<'dia' | 'semana' | 'mes'>('semana');
  semanaInicio = signal<string>(this.getMonday(new Date()));
  recursoAgenda = signal<number | null>(-1);
  recursosAgenda = signal<IReservaData[][]>([]);
  todosAgenda = signal<IReservaData[]>([]);
  mesActual = signal<number>(new Date().getMonth());
  anioActual = signal<number>(new Date().getFullYear());
  mesData = signal<IReservaData[]>([]);

  // Modals
  modalCrearRecurso = model(false);
  modalEditarRecurso = model(false);
  modalEliminarRecurso = model(false);
  modalHorarios = model(false);
  modalCrearReserva = model(false);
  modalVerReserva = model(false);
  reservaViendo = signal<IReservaData | undefined>(undefined);

  // Computed stats
  stats = computed(() => {
    const r = this.reservas();
    return {
      total: r.length,
      pendientes: r.filter((x) => x.estado === EEstadoReserva.PENDIENTE).length,
      confirmadas: r.filter((x) => x.estado === EEstadoReserva.CONFIRMADA).length,
      hoy: r.filter((x) => x.fecha === this.hoy()).length,
    };
  });

  estadoOpciones = [
    { label: 'Todos', value: null },
    { label: 'Pendiente', value: EEstadoReserva.PENDIENTE },
    { label: 'Confirmada', value: EEstadoReserva.CONFIRMADA },
    { label: 'Cancelada', value: EEstadoReserva.CANCELADA },
    { label: 'Completada', value: EEstadoReserva.COMPLETADA },
  ];

  estadoLabels: Record<string, string> = {
    [EEstadoReserva.PENDIENTE]: 'Pendiente',
    [EEstadoReserva.CONFIRMADA]: 'Confirmada',
    [EEstadoReserva.CANCELADA]: 'Cancelada',
    [EEstadoReserva.COMPLETADA]: 'Completada',
  };

  estadoClase: Record<string, string> = {
    [EEstadoReserva.PENDIENTE]: 'bg-amber-100 text-amber-700',
    [EEstadoReserva.CONFIRMADA]: 'bg-emerald-100 text-emerald-700',
    [EEstadoReserva.CANCELADA]: 'bg-red-100 text-red-600',
    [EEstadoReserva.COMPLETADA]: 'bg-blue-100 text-blue-700',
  };

  private hoy(): string {
    return new Date().toISOString().split('T')[0];
  }

  private getMonday(date: Date): string {
    const d = new Date(date);
    const day = d.getDay();
    d.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
    return d.toISOString().split('T')[0];
  }

  // Recursos filtrados
  recursosFiltrados = computed(() => {
    const q = (this.searchRecurso() ?? '').toLowerCase();
    if (!q) return this.recursos();
    return this.recursos().filter((r) => r.nombre?.toLowerCase().includes(q));
  });

  // Recursos
  cargarRecursos() {
    this.loadingRecursos.set(true);
    this.api.listarRecursos().subscribe({
      next: (data) => {
        this.recursos.set(data.filter((r: any) => r.activo !== false));
        this.loadingRecursos.set(false);
        if (data.length > 0 && !this.recursoAgenda()) {
          this.recursoAgenda.set(-1);
        }
      },
      error: (err) => {
        this.loadingRecursos.set(false);
        if (err.status === 401) return;
        this.toast.show('top-right', 'error', 'Error', 'No se pudieron cargar los recursos');
      },
    });
  }

  onCrearRecurso(data?: IReservaRecursoData) {
    this.modalCrearRecurso.set(false);
    if (data) this.cargarRecursos();
  }

  onEditarRecurso(data?: IReservaRecursoData) {
    this.modalEditarRecurso.set(false);
    if (data) this.cargarRecursos();
  }

  solicitarEliminarRecurso(recurso: IReservaRecursoData) {
    this.recursoSeleccionado.set(recurso);
    this.modalEliminarRecurso.set(true);
  }

  confirmarEliminarRecurso() {
    const r = this.recursoSeleccionado();
    if (!r) return;
    this.api.eliminarRecurso(r.id).subscribe({
      next: () => {
        this.toast.show('top-right', 'success', 'Éxito', `Recurso "${r.nombre}" eliminado`);
        this.modalEliminarRecurso.set(false);
        this.cargarRecursos();
      },
      error: (err) => {
        if (err.status === 401) return;
        this.toast.show('top-right', 'error', 'Error', err.error?.message ?? 'No se pudo eliminar');
      },
    });
  }

  abrirHorarios(recurso: IReservaRecursoData) {
    this.recursoSeleccionado.set(recurso);
    this.modalHorarios.set(true);
  }

  // Reservas
  cargarReservas() {
    this.loadingReservas.set(true);
    const filtros: IFiltrarReservasReq = {};
    if (this.estadoFiltro()) filtros.estado = this.estadoFiltro()!;
    if (this.recursoFiltro()) filtros.recurso_id = this.recursoFiltro()!;
    this.api.listarReservas(filtros).subscribe({
      next: (data) => {
        this.reservas.set(data.data);
        this.loadingReservas.set(false);
      },
      error: (err) => {
        this.loadingReservas.set(false);
        if (err.status === 401) return;
        this.toast.show('top-right', 'error', 'Error', 'No se pudieron cargar las reservas');
      },
    });
  }

  cambiarEstadoReserva(reserva: IReservaData) {
    const nuevoEstado =
      reserva.estado === EEstadoReserva.PENDIENTE
        ? EEstadoReserva.CONFIRMADA
        : reserva.estado === EEstadoReserva.CONFIRMADA
          ? EEstadoReserva.COMPLETADA
          : EEstadoReserva.CANCELADA;
    this.api.cambiarEstado(reserva.id, { estado: nuevoEstado }).subscribe({
      next: () => {
        this.toast.show('top-right', 'success', 'Éxito', `Reserva ${nuevoEstado.toLowerCase()}`);
        this.refrescarTodo();
      },
      error: (err) => {
        if (err.status === 401) return;
        this.toast.show(
          'top-right',
          'error',
          'Error',
          err.error?.message ?? 'No se pudo cambiar el estado',
        );
      },
    });
  }

  cancelarReserva(reserva: IReservaData) {
    this.api.cambiarEstado(reserva.id, { estado: EEstadoReserva.CANCELADA }).subscribe({
      next: () => {
        this.toast.show('top-right', 'success', 'Éxito', 'Reserva cancelada');
        this.refrescarTodo();
      },
      error: (err) => {
        if (err.status === 401) return;
        this.toast.show('top-right', 'error', 'Error', err.error?.message ?? 'No se pudo cancelar');
      },
    });
  }

  refrescarTodo() {
    this.cargarReservas();
    this.cargarAgendaActiva();
  }

  cargarAgendaActiva() {
    if (this.vistaAgenda() === 'semana') this.cargarAgendaSemanal();
    else if (this.vistaAgenda() === 'mes') this.cargarAgendaMensual();
    else this.cargarAgenda();
  }

  verReserva(reserva: IReservaData) {
    this.reservaViendo.set(reserva);
    this.modalVerReserva.set(true);
  }

  // Agenda
  cambiarSemana(direccion: number) {
    const d = new Date(this.semanaInicio() + 'T00:00:00');
    d.setDate(d.getDate() + direccion * 7);
    this.semanaInicio.set(d.toISOString().split('T')[0]);
    this.cargarAgendaSemanal();
  }

  cambiarMes(direccion: number) {
    let mes = this.mesActual() + direccion;
    let anio = this.anioActual();
    if (mes < 0) {
      mes = 11;
      anio--;
    }
    if (mes > 11) {
      mes = 0;
      anio++;
    }
    this.mesActual.set(mes);
    this.anioActual.set(anio);
    this.cargarAgendaMensual();
  }

  private formatFecha(fecha: string | Date): string {
    if (fecha instanceof Date) {
      return fecha.toISOString().split('T')[0];
    }
    if (typeof fecha === 'string' && fecha.includes('T')) {
      return fecha.split('T')[0];
    }
    return fecha;
  }

  irHoy() {
    const hoy = new Date();
    this.semanaInicio.set(this.getMonday(hoy));
    this.fechaAgenda.set(hoy.toISOString().split('T')[0]);
    this.mesActual.set(hoy.getMonth());
    this.anioActual.set(hoy.getFullYear());
    if (this.vistaAgenda() === 'semana') this.cargarAgendaSemanal();
    else if (this.vistaAgenda() === 'mes') this.cargarAgendaMensual();
    else this.cargarAgenda();
  }

  cambiarVista(vista: 'dia' | 'semana' | 'mes') {
    this.vistaAgenda.set(vista);
    if (vista === 'semana') this.cargarAgendaSemanal();
    else if (vista === 'mes') this.cargarAgendaMensual();
    else this.cargarAgenda();
  }

  mesNombres = [
    'Enero',
    'Febrero',
    'Marzo',
    'Abril',
    'Mayo',
    'Junio',
    'Julio',
    'Agosto',
    'Septiembre',
    'Octubre',
    'Noviembre',
    'Diciembre',
  ];

  getMesLabel(): string {
    return `${this.mesNombres[this.mesActual()]} ${this.anioActual()}`;
  }

  getSemanasDelMes(): string[][] {
    const year = this.anioActual();
    const month = this.mesActual();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startPad = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
    const dias: string[] = [];
    for (let i = 0; i < startPad; i++) dias.push('');
    for (let d = 1; d <= lastDay.getDate(); d++) {
      dias.push(`${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
    }
    const semanas: string[][] = [];
    for (let i = 0; i < dias.length; i += 7) {
      semanas.push(dias.slice(i, i + 7));
    }
    return semanas;
  }

  getReservasDelDia(fecha: string): IReservaData[] {
    return this.mesData().filter((r) => r.fecha === fecha);
  }

  getDiaNumero(fechaStr: string): number {
    if (!fechaStr) return 0;
    return new Date(fechaStr + 'T00:00:00').getDate();
  }

  getSemanaLabel(): string {
    const d = new Date(this.semanaInicio() + 'T00:00:00');
    const fin = new Date(d);
    fin.setDate(fin.getDate() + 6);
    return `${d.getDate()}/${d.getMonth() + 1} - ${fin.getDate()}/${fin.getMonth() + 1}`;
  }

  getDiasSemana(): string[] {
    const dias: string[] = [];
    const d = new Date(this.semanaInicio() + 'T00:00:00');
    for (let i = 0; i < 7; i++) {
      const fecha = new Date(d);
      fecha.setDate(fecha.getDate() + i);
      dias.push(fecha.toISOString().split('T')[0]);
    }
    return dias;
  }

  getDiaLabel(fechaStr: string): string {
    const d = new Date(fechaStr + 'T00:00:00');
    const dia = d.getDay() === 0 ? 6 : d.getDay() - 1;
    return DIAS_SEMANA_LABELS[dia]?.substring(0, 3) ?? '';
  }

  isHoy(fechaStr: string): boolean {
    return fechaStr === this.hoy();
  }

  cargarAgenda() {
    const fecha = this.formatFecha(this.fechaAgenda());
    this.reservas.set([]);
    this.api
      .listarReservas({
        fecha_desde: fecha,
        fecha_hasta: fecha,
        size: 200,
      })
      .subscribe({
        next: (data) => {
          const todas = data.data ?? [];
          const rid = this.recursoAgenda();
          this.reservas.set(rid && rid > 0 ? todas.filter((r) => r.recurso_id === rid) : todas);
        },
        error: (err) => {
          if (err.status === 401) return;
          this.toast.show('top-right', 'error', 'Error', 'No se pudo cargar la agenda');
        },
      });
  }

  cargarAgendaSemanal() {
    const rid = this.recursoAgenda();
    const dias = this.getDiasSemana();
    this.recursosAgenda.set([]);
    this.reservas.set([]);
    const todas: IReservaData[][] = [];
    let completadas = 0;
    dias.forEach((fecha, idx) => {
      this.api
        .listarReservas({
          fecha_desde: this.formatFecha(fecha),
          fecha_hasta: this.formatFecha(fecha),
          size: 200,
        })
        .subscribe({
          next: (data) => {
            const raw = data.data ?? [];
            todas[idx] = rid && rid > 0 ? raw.filter((r) => r.recurso_id === rid) : raw;
            completadas++;
            if (completadas === 7) this.recursosAgenda.set(todas);
          },
        });
    });
  }

  cargarAgendaMensual() {
    this.reservas.set([]);
    const rid = this.recursoAgenda();
    const year = this.anioActual();
    const month = this.mesActual();
    const desde = `${year}-${String(month + 1).padStart(2, '0')}-01`;
    const ultimo = new Date(year, month + 1, 0);
    const hasta = `${year}-${String(month + 1).padStart(2, '0')}-${String(ultimo.getDate()).padStart(2, '0')}`;
    this.api.listarReservas({ fecha_desde: desde, fecha_hasta: hasta, size: 500 }).subscribe({
      next: (data) => {
        const raw = data.data ?? [];
        this.mesData.set(rid && rid > 0 ? raw.filter((r) => r.recurso_id === rid) : raw);
      },
    });
  }

  // Recursos dropdown (with "Todos" option)
  get recursosOpciones() {
    return [
      { label: '📋 Todos los recursos', value: -1 },
      ...this.recursos().map((r) => ({ label: r.nombre, value: r.id })),
    ];
  }

  get recursosOpcionesFiltro() {
    return [
      { label: 'Todos', value: null as number | null },
      ...this.recursos().map((r) => ({ label: r.nombre, value: r.id as number | null })),
    ];
  }

  recursoColor(idx: number): string {
    const colors = [
      '#8b5cf6',
      '#06b6d4',
      '#f59e0b',
      '#10b981',
      '#ef4444',
      '#3b82f6',
      '#ec4899',
      '#84cc16',
    ];
    return colors[idx % colors.length];
  }

  getRecursoNombre(id: number): string {
    return this.recursos().find((r) => r.id === id)?.nombre ?? '';
  }

  nuevaReservaRapida(recursoId: number) {
    this.recursoAgenda.set(recursoId);
    this.modalCrearReserva.set(true);
  }

  onCrearReserva(data?: IReservaData) {
    this.modalCrearReserva.set(false);
    if (data) this.refrescarTodo();
  }

  onSeleccionarRecursoAgenda(id: number) {
    this.recursoAgenda.set(id);
    if (this.vistaAgenda() === 'semana') this.cargarAgendaSemanal();
    else if (this.vistaAgenda() === 'mes') this.cargarAgendaMensual();
    else this.cargarAgenda();
  }

  // Update tab & load data
  onTabChange(value: string | number | undefined) {
    const v = String(value ?? 'recursos');
    this.tabActivo.set(v);
    if (v === 'recursos') this.cargarRecursos();
    else if (v === 'reservas') this.cargarReservas();
    else if (v === 'agenda') this.cargarAgendaActiva();
  }
}
